import { useEffect, useMemo, useState } from 'react';

import { getUser } from '../../modules/contexts/Auth/RequireAuth';
import { ParteEnvolvida } from '../../modules/types/Aquisicao';
import {
  getTipoEnvolvimentoLabelPorSlug,
  getTipoEnvolvimentoOptions,
  getTipoEnvolvimentoPorCodigo,
  getTipoEnvolvimentoSlug,
} from '../../modules/types/TipoEnvolvimentoEnum';
import { EnumOption, LogData } from '../../modules/types/types';
import { Fornecedor } from '../../pages/daf/Fornecedores/types';
import {
  handleCreateParteEnvolvida,
  handleDeleteParteEnvolvida,
  handleUpdateParteEnvolvida,
} from '../../services/aquisicoes/parteEnvolvidaService';
import { getAllPartesEnvolvidasPorEntidade } from '../../services/aquisicoes/parteEnvolvidaService';
import { getAllFornecedoresSimplificado } from '../../services/fornecedoresDAF/fornecedorService';
import { isHttpError } from '../../services/httpClient';
import theme from '../../theme';
import {
  formataNomeMeioSiglas,
  formatDate,
  sanitizarForm,
} from '../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../utils/swal';
import BarraInferiorBotoes from '../BarraInferiorBotoes';
import BoxConteudo from '../BoxConteudo';
import CustomButton from '../CustomButton';
import CustomCol from '../CustomCol';
import CustomRow from '../CustomRow';
import Input from '../InputNew';
import Select from '../InputNew/Select';
import Log from '../Log/Log';
import Tabela from '../Tabela';
import WorkspaceBotoes from '../WorkspaceBotoes';
import WorkspaceFicha from '../WorkspaceFicha';
import { paisesOptions } from './paisesOptions';
import CustomOffcanvas from '../CustomOffcanvas';

interface ParteEnvolvidaComponenteProps {
  tab: string;
  id_tab: string;
}

const headers = [
  { text: '', width: 1, key: '' },
  { text: 'ID', width: 2, key: 'idPA' },
  { text: 'Tipo Envolvimento', width: 12, key: 'tipoEnvolvimentoPA' },
  { text: 'CNPJ / País', width: 15, key: 'cnpjPA' },
  { text: 'Razão Social', width: 70, key: 'razaoSocialPA' },
];

const valoresExibidos: Record<string, string> = {
  id: 'ID',
  dataRegistro: 'Data de Registro',
  dataAtualizacao: 'Última Atualização',
  dataUltimaVisualizacao: 'Última Visualização',
  dataUltimaAtualizacao: 'Última Atualização',
  nEdicoes: 'Edições',
  nVisualizacoes: 'Visualizações',
  nVisualizadores: 'Visualizadores',
  usuarioRegistro: 'Responsável pelo Registro',
  usuarioAtualizacao: 'Responsável Ult. Atualização',
  usuarioUltimaVisualizacao: 'Responsável Ult. Visualização',
  observacoes: 'Observações',

  cnpj: 'CNPJ',
  razaoSocial: 'Razão Social',
  tipoEnvolvimento: 'Tipo de Envolvimento',
  paisOrigem: 'País de Origem',
  estrangeiro: 'Estrangeiro',

  separador: '',
  separadorLog: '',
};

function ParteEnvolvidaComponente({
  tab,
  id_tab,
}: ParteEnvolvidaComponenteProps) {
  const user = getUser();
  const userID = user.sub;

  const [updating, setUpdating] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [logData, setLogData] = useState<LogData>();

  const [tipoEnvolvimentoValue, setTipoEnvolvimentoValue] = useState(0);
  const [fornecedoresOptionsData, setFornecedoresOptionsData] = useState<
    Fornecedor[]
  >([]);
  const [data, setData] = useState<ParteEnvolvida[]>();

  const [form, setForm] = useState<ParteEnvolvida>({
    id: 0,
    tipoEnvolvimento: '',
    cnpj: '',
    razaoSocial: '',
    observacoes: '',
    tabId: parseInt(id_tab, 10),
    fornecedorId: 0,
    usuarioId: userID,
    paisOrigem: '',
    estrangeiro: false,
    razaoSocialEstrangeiro: '',
  });
  const isEstrangeiro = form.estrangeiro ?? false;
  const estrangeiroSelectOptions = useMemo(
    () => [
      { value: 'false', label: 'Não' },
      { value: 'true', label: 'Sim' },
    ],
    [],
  );
  const paisSelectOptions = useMemo(
    () => [{ value: '', label: 'Selecione' }, ...paisesOptions],
    [],
  );
  // tipoAquisicao: props.tipoAquisicao,

  const fetchData = async () => {
    try {
      const fornecedoresSimplificado = await getAllFornecedoresSimplificado();
      setFornecedoresOptionsData(fornecedoresSimplificado);
      const partesEnvolvidas = await getAllPartesEnvolvidasPorEntidade(
        tab,
        Number(id_tab),
      );
      setData(partesEnvolvidas);
    } catch (error) {
      console.error('Erro ao obter dados:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id_tab]);

  // Funções handle
  const handleClearForm = () => {
    setForm((prev) => ({
      ...prev,
      id: 0,
      tipoEnvolvimento: '',
      observacoes: '',
      cnpj: '',
      razaoSocial: '',
      tabId: parseInt(id_tab, 10),
      fornecedorId: 0,
      fornecedor: undefined,
      paisOrigem: '',
      estrangeiro: false,
      razaoSocialEstrangeiro: '',
    }));
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
    setTipoEnvolvimentoValue(0);
  };

  const aplicarFornecedorSelecionado = (fornecedorId: number) => {
    if (Number.isNaN(fornecedorId)) {
      setForm((prev) => ({
        ...prev,
        fornecedorId: 0,
        cnpj: '',
        razaoSocial: '',
      }));
      return;
    }
    const selectedFornecedor = fornecedoresOptionsData.find(
      (fornecedor) => fornecedor.id === fornecedorId,
    );

    if (selectedFornecedor) {
      setForm((prev) => ({
        ...prev,
        fornecedorId,
        cnpj: selectedFornecedor.cnpj,
        razaoSocial: selectedFornecedor.razaoSocial ?? '',
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        fornecedorId: 0,
        cnpj: '',
        razaoSocial: '',
      }));
    }
  };

  const atualizarFlagEstrangeiro = (valor: boolean) => {
    setForm((prev) => ({
      ...prev,
      estrangeiro: valor,
      fornecedorId: valor ? 0 : prev.fornecedorId,
      fornecedor: valor ? undefined : prev.fornecedor,
      cnpj: valor ? '' : prev.cnpj,
      paisOrigem: valor ? prev.paisOrigem : '',
      razaoSocial: valor ? '' : prev.razaoSocial,
      razaoSocialEstrangeiro: valor ? '' : prev.razaoSocialEstrangeiro,
    }));
  };

  const fornecedorOptions: EnumOption[] = [
    { value: '0', label: 'Não informado' },
    ...fornecedoresOptionsData.map((item, index) => ({
      value: item.id?.toString() ?? index.toString(),
      label: item.cnpj,
    })),
  ];
  const fornecedorRazaoOptions: EnumOption[] = [
    { value: '0', label: 'Não informado' },
    ...fornecedoresOptionsData.map((item, index) => ({
      value: item.id?.toString() ?? index.toString(),
      label: item.razaoSocial ?? 'Sem razão social',
    })),
  ];

  // Tabela
  const tableData =
    data?.map((item: ParteEnvolvida) => {
      const isEstrangeiro = item.estrangeiro ?? false;
      return {
        id: item.id,
        tipoEnvolvimento:
          getTipoEnvolvimentoLabelPorSlug(item.tipoEnvolvimento) || '',
        cnpj: isEstrangeiro
          ? `País: ${item.paisOrigem ?? 'Não informado'}`
          : (item.fornecedor?.cnpj ?? 'Não encontrado'),
        razaoSocial: isEstrangeiro
          ? (item.razaoSocial ??
            item.razaoSocialEstrangeiro ??
            'Não encontrado')
          : (item.fornecedor?.razaoSocial ?? 'Não encontrado'),
      };
    }) || [];

  const offcanvasData =
    data?.map((item: ParteEnvolvida) => {
      const isEstrangeiro = item.estrangeiro ?? false;
      return {
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),

        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(
            item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
          ),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        separadorLog: '',
        cnpj: isEstrangeiro
          ? 'Não aplicável'
          : (item.fornecedor?.cnpj ?? 'Não encontrado'),
        razaoSocial: isEstrangeiro
          ? (item.razaoSocial ??
            item.razaoSocialEstrangeiro ??
            'Não encontrado')
          : (item.fornecedor?.razaoSocial ?? 'Não encontrado'),
        paisOrigem: isEstrangeiro
          ? (item.paisOrigem ?? 'Não informado')
          : 'Brasil',
        estrangeiro: isEstrangeiro ? 'Sim' : 'Não',
        tipoEnvolvimento:
          getTipoEnvolvimentoLabelPorSlug(item.tipoEnvolvimento) || '',
        separador: '',
        observacoes: item.observacoes,
      };
    }) || [];

  //Funções Handle
  const handleShowModal = () => {
    setShowModal((prev) => !prev);
    handleClearForm();
  };

  const handleRowClick = (rowOrId: any) => {
    const rawId =
      typeof rowOrId === 'object' && rowOrId !== null ? rowOrId.id : rowOrId;
    const parsedId = Number(rawId);
    if (!rawId || Number.isNaN(parsedId)) return;

    handleShowModal();
    setUpdating(true);
    const filteredItem = data?.find((item) => item.id === parsedId);

    if (filteredItem) {
      const registroEstrangeiro = filteredItem.estrangeiro ?? false;
      setForm({
        ...filteredItem,
        fornecedorId: registroEstrangeiro
          ? 0
          : (filteredItem.fornecedor?.id ?? 0),
        tipoEnvolvimento: filteredItem.tipoEnvolvimento,
        cnpj: registroEstrangeiro ? '' : (filteredItem.fornecedor?.cnpj ?? ''),
        razaoSocial: registroEstrangeiro
          ? (filteredItem.razaoSocial ??
            filteredItem.razaoSocialEstrangeiro ??
            '')
          : (filteredItem.fornecedor?.razaoSocial ?? ''),
        observacoes: filteredItem.observacoes ?? 'Não informado',
        fornecedor: registroEstrangeiro
          ? undefined
          : (filteredItem.fornecedor ?? undefined),
        paisOrigem: filteredItem.paisOrigem ?? '',
        estrangeiro: registroEstrangeiro,
        razaoSocialEstrangeiro: filteredItem.razaoSocialEstrangeiro ?? '',
      });
      setTipoEnvolvimentoValue(
        getTipoEnvolvimentoPorCodigo(filteredItem.tipoEnvolvimento ?? '') ?? 0,
      );
      setLogData({
        id: filteredItem.id,
        usuarioRegistro: filteredItem.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro ?? '',
        dataAtualizacao: filteredItem.dataUltimaAtualizacao ?? '',
        nEdicoes: filteredItem.nEdicoes ?? 0,
      });
    }
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    if (name === 'tipoEnvolvimento') {
      setTipoEnvolvimentoValue(parseInt(value, 10));
      return;
    } else if (name === 'estrangeiro') {
      atualizarFlagEstrangeiro(value === 'true');
      return;
    } else if (name === 'paisOrigem') {
      setForm((prev) => ({
        ...prev,
        paisOrigem: value,
      }));
      return;
    } else if (name === 'fornecedorId' && !isEstrangeiro) {
      const fornecedorId = parseInt(value, 10);
      aplicarFornecedorSelecionado(fornecedorId);
      return;
    } else if (name === 'razaoSocial' && isEstrangeiro) {
      setForm((prev) => ({
        ...prev,
        razaoSocial: value,
        razaoSocialEstrangeiro: value,
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = (form: ParteEnvolvida): boolean => {
    const erros: string[] = [];
    const entidadeEstrangeira = form.estrangeiro ?? false;

    if (!entidadeEstrangeira && !form.fornecedorId) {
      erros.push('<b>CNPJ</b> é obrigatório para distribuidores nacionais.');
    }

    if (entidadeEstrangeira) {
      if (!form.razaoSocial?.trim()) {
        erros.push(
          '<b>Nome/Razão Social</b> é obrigatório para distribuidores estrangeiros.',
        );
      }

      if (!form.paisOrigem) {
        erros.push('<b>País de Origem</b> é obrigatório.');
      }
    }

    if (!tipoEnvolvimentoValue) {
      erros.push('<b>Tipo de Envolvimento</b> é obrigatório.');
    }

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }

    return true;
  };

  const formatForm = (form: ParteEnvolvida) => {
    const entidadeEstrangeira = form.estrangeiro ?? false;
    const formAjustado = {
      ...form,
      tipoEnvolvimento: getTipoEnvolvimentoSlug(tipoEnvolvimentoValue),
      usuarioId: form.usuarioId,
      tabId: parseInt(id_tab, 10),
      fornecedorId: entidadeEstrangeira ? undefined : Number(form.fornecedorId),
      paisOrigem: entidadeEstrangeira ? form.paisOrigem : undefined,
      razaoSocial: form.razaoSocial?.trim() ?? '',
      razaoSocialEstrangeiro: entidadeEstrangeira
        ? (form.razaoSocial?.trim() ?? '')
        : undefined,
    };

    return formAjustado;
  };

  const handleCreate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      await handleCreateParteEnvolvida(formToSend, tab);

      swalSave();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao salvar registro:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      const response = await handleUpdateParteEnvolvida(formToSend);

      swalAtualizacao('Sucesso', response.data.message);
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao atualizar registro:', err);
    }
  };

  const handleDelete = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      await handleDeleteParteEnvolvida(form.id);
      swalDeleteConfirm();
      fetchData();
      setTimeout(() => handleShowModal(), 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao excluir registro:', err);
    }
  };

  return (
    <BoxConteudo boxTitulo={'Partes Envolvidas'} fechado>
      <CustomRow>
        <CustomCol width="100%">
          <Tabela
            headers={headers}
            dataTable={tableData}
            displayValues={valoresExibidos}
            onRowClick={handleRowClick}
            dataOffCanvas={offcanvasData}
            botaoAbrirOffCanvas={false}
            showPagination={false}
          />
        </CustomCol>
      </CustomRow>
      <CustomRow>
        <CustomCol className="d-flex justify-content-end">
          <CustomButton
            label="Novo Registro"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={() => {
              setUpdating(false);
              handleShowModal();
            }}
            disabled={id_tab !== 'undefined' ? false : true}
          />
        </CustomCol>
      </CustomRow>

      {/* Modal /*/}
      <CustomOffcanvas
        title={'Parte Envolvida'}
        show={showModal}
        onClose={handleShowModal}
        width="70vw"
      >
        <WorkspaceFicha>
          <Log {...logData} entity="TB_PARTE_ENVOLVIDA" id={Number(form.id)} />
          <BoxConteudo boxTitulo="Identificação da Parte">
            <CustomRow
              style={{
                marginTop: '10px',
                justifyContent: 'flex-start',
                gap: '20px',
              }}
            >
              <CustomCol width="200px">
                <Select
                  name="tipoEnvolvimento"
                  options={getTipoEnvolvimentoOptions()}
                  label="Tipo de Envolvimento"
                  value={tipoEnvolvimentoValue}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <Select
                  name="estrangeiro"
                  options={estrangeiroSelectOptions}
                  label="Estrangeiro"
                  value={isEstrangeiro ? 'true' : 'false'}
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <Select
                  name="paisOrigem"
                  options={paisSelectOptions}
                  label="País de Origem"
                  value={form.paisOrigem}
                  onChange={handleChange}
                  disabled={!isEstrangeiro}
                  required={isEstrangeiro}
                />
              </CustomCol>
            </CustomRow>
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="250px">
                <Select
                  name="fornecedorId"
                  options={fornecedorOptions}
                  label="CNPJ do Distribuidor (nacional)"
                  value={isEstrangeiro ? '0' : form.fornecedorId}
                  onChange={handleChange}
                  disabled={isEstrangeiro}
                  required={!isEstrangeiro}
                />
              </CustomCol>
              {!isEstrangeiro ? (
                <CustomCol>
                  <Select
                    name="fornecedorId"
                    options={fornecedorRazaoOptions}
                    label="Razão Social"
                    value={form.fornecedorId}
                    onChange={handleChange}
                    required
                  />
                </CustomCol>
              ) : (
                <CustomCol>
                  <Input
                    id="razaoSocial"
                    name="razaoSocial"
                    value={form.razaoSocial}
                    label="Nome / Razão Social"
                    onChange={handleChange}
                    required
                  />
                </CustomCol>
              )}
            </CustomRow>
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={form.observacoes}
                  label="Observações"
                  linhas={3}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                onClickDelete={handleDelete}
                save
                onClickSave={handleUpdate}
              />
            ) : (
              <WorkspaceBotoes inserir onClickInserir={handleCreate} />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
}

export default ParteEnvolvidaComponente;
